/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// Internal dependencies
import { fullyDecodeURI } from '@rakuten/common-util/url';
import { TAG, SECURITY_ERROR_CODES } from './security.constants';
import SecurityServiceError from './security.error';


// -----------------------------------------------------------------------------
// CONFIGURATION
// -----------------------------------------------------------------------------

// The backend URL from which to load security related information
// TODO: Make this externally configurable and not dependent on URL
let URL_BOSH;
const href = new URL(window.location.href);
if (
  href?.hostname?.endsWith('prodref.mytolino.com') ||
  [ '127.0.0.1', 'localhost' ].indexOf(href?.hostname) > -1
) {
  URL_BOSH = 'https://bosh.prodref.pageplace.de/bosh/rest/v2/resellerconfig';
} else {
  URL_BOSH = 'https://bosh.pageplace.de/bosh/rest/v2/resellerconfig';
}

// The reseller id to use when communicating with the backend
const RESELLER_ID = 555

// The client type to use when communicating with the backend
const CLIENT_TYPE = 'TOLINO_SECURITY';

const WHITELIST_LOCAL = [
  '127.0.0.1',
  'localhost'
];


// -----------------------------------------------------------------------------
// SECURITY SERVICE
// -----------------------------------------------------------------------------

export function createSecurityService(fetchService, logger) {

  // ---------------------------------------------------------------------------
  // PRIVATE METHODS
  // ---------------------------------------------------------------------------

  /**
   * Loads the domain whitelist from backend. This list can be used to validate
   * URLs against a set of domains from which it is okay to load content.
   *
   * @return {Promise<Response>} The promise to fetch the URL whitelist from backend
   */
  function loadDomainWhiteList() {
    logger.info(`${TAG} Loading domain whitelist from backend`);

    return fetchService.fetch({
      url: URL_BOSH,
      options: {
        headers: {
          'reseller_id': RESELLER_ID,
          'client_type': CLIENT_TYPE
        }
      }
    });
  }


  /**
   * Validates an URL against a set of domains to see if it is okay to load
   * content from that domain
   *
   * @param {string} url The URL to validate
   * @return {Promise<boolean>} Whether or not the URL is from valid domain
   * @throws
   */
  function validateURLAgainstWhiteList(url) {
    url = new URL(fullyDecodeURI(url));

    // No need to check local URLs
    if (WHITELIST_LOCAL.indexOf(url.hostname) > -1) {
      logger.info(`${TAG} No need to validate local URL "${url}" against domain whitelist`);
      return Promise.resolve(true);
    }

    return loadDomainWhiteList().then(
      response => {
        logger.info(`${TAG} Validating URL "${url} against domain whitelist ...`);

        let whitelistedDomains = response?.config?.SAMPLEREADER_URL_WHITELIST;
        if (whitelistedDomains) {
          whitelistedDomains = whitelistedDomains.split(', ');

          // We only need the domain part of the hostname, ot any subdomains etc.
          const hostname = url.hostname.split('.').splice(-2).join('.');

          if (!hostname || whitelistedDomains.indexOf(hostname) < 0) {
            logger.log("[VARIABLE] SECURITY_ERROR_CODES.URL_EPUB_NOT_ALLOWED = ", SECURITY_ERROR_CODES.URL_EPUB_NOT_ALLOWED);
            throw new SecurityServiceError(
              SECURITY_ERROR_CODES.URL_EPUB_NOT_ALLOWED,
              {
                details: { whitelistedDomains, hostname, epub_url: url.href }
              }
            );
          }
        }

        logger.info(`${TAG} Validating URL "${url} against domain whitelist succeeded`);
        return true;
      },
      () => {
        throw new SecurityServiceError(SECURITY_ERROR_CODES.UNABLE_TO_LOAD_WHITELIST);
      }
    );
  }


  /**
   * Validates a list of URLs against a set of domains to see if it is okay to
   * load content from that domain.
   *
   * @param {Array<string>} urls List of URLs to validate
   * @return {Promise<boolean>} Whether or not all URLs are from a valid domain}
   */
  function validateURLsAgainstWhiteList(urls) {
    const promises = [ ];

    for (let i = 0, ii = urls.length; i < ii; i++) {
      promises.push(validateURLAgainstWhiteList(urls[i]));
    }

    return Promise.allSettled(promises).then(
      results => {
        results.forEach(result => {
          if (result.status !== "fulfilled") {
            throw new Error(result.reason);
          }
        });

        return true;
      }
    );
  }


  // ---------------------------------------------------------------------------
  // PUBLIC API
  // ---------------------------------------------------------------------------

  return {
    SecurityServiceError,
    validateURLAgainstWhiteList,
    validateURLsAgainstWhiteList
  };
}


// -----------------------------------------------------------------------------
// SERVICE REGISTRATION
// -----------------------------------------------------------------------------

//service config for service locator
export const config = ['security', [ 'fetch', 'logger' ], createSecurityService];
